<!-- Note that there is a huge downside to using a combined input for email / phone in that we don't get to set the field type correctly, thus we miss out on the browser's autocomplete capabilities. This may not matter for most people, because at least for Chrome, automcomplete refers to your *own* contact information that you explicitly added to the browser as opposed to your address book. You can get clever with hidden input fields, but these work inconsistently cross-browser. I think the best solution is just to have the toggle buttons for the type of data you're inputting, and automatically switch them if the type of data looks wrong. -->

<template>
  <div class="d-flex relative" @focusin="focus = true" @focusout="onFocusOut">
    <template v-if="!hideContacts">
      <template v-if="showContacts">
        <contacts-dialog
          v-if="isSignedIn"
          single
          @select="v => { staged = $options.filters['email-phone'](v); showContacts = false; }"
          @close="showContacts = false"
        />
        <sign-in-dialog v-else :value="true" @input="$nextTick(() => showContacts = !!isSignedIn)">
          <template v-slot:title>Ellacard contacts</template>
          <template v-slot:subtitle>Sign in to access / import your contacts</template>
        </sign-in-dialog>
      </template>

      <div class="flex-shrink-0 btn-container" v-bind:class="{ fixed, focus }">
        <v-btn
          class="btn-tooltip left persistent px-2"
          hover icon dark
          @click="showContacts = true"
        >
          <v-icon v-html="mdiCardAccountPhone" />
        </v-btn>
      </div>
    </template>

    <v-text-field
      ref="input"
      class="white inline-error"
      type="text" outlined flat dense placeholder="Email address / phone number" autocomplete="do-not-autocomplete"
      :hint="hint"
      :persistent-hint="!!hint"
      :label="label"
      :readonly="readonly"
      :disabled="disabled"
      :rules="[
        v => !e164 || e164.startsWith('+1') || 'US / CA phone numbers only',
        v => !required || !!target,
        v => !counts || ((counts[ e164 || staged.toLowerCase() ] || 0) <= 1) || 'Duplicate',
        ...rules
      ]"
      :append-icon="(isValidEmail && mdiAt) || (isValidPhone && mdiMessage) || null"
      v-model="staged"
      @paste="onPaste"
      @focus="e => $emit('focus', e)"
      @blur="
        e => {
          if (isValidPhone)
            staged = $options.filters['email-phone'](e164);
          $emit('blur', e);
        }
      "
      @keydown.enter="e => $emit('enter', e)"
    />
  </div>
</template>


<style scoped>
.btn-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 0;
  opacity: 0;
  transition: width .2s, opacity .2s;
}
.btn-container.focus,
.btn-container.fixed {
  width: 48px;
}
.btn-container.focus {
  opacity: 1;
}
.btn-tooltip {
  cursor: pointer;
  left: 0;
  width: 40px;
}
.btn-tooltip:hover {
  box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12);
}
</style>


<script>
// import loadScript from '@/utils/load-script.js';
import ContactsDialog from '@/components/ContactsDialog.vue';
import SignInDialog from '@/components/SignInDialog.vue';
import { EMAIL_REGEX } from '@/utils/email.js';
import { mdiCardAccountPhone, mdiAt, mdiMessage, mdiHelp } from '@mdi/js';

function getE164(v) {
  // Ideally, all phone numbers would be entered in e164 format including the '+1' prefix. However, we also want to
  // handle phone numbers that omit the prefix (assume +1) or phone numbers that even omit the '+' character.
  if (/[^\d\W]/.test(v))
    return '';
  const e164 = v.replace(/[^\+0-9]/g, '');
  if ((e164.length == 10) && (e164[0] != '+'))
    return '+1' + e164;
  if ((e164.length == 11) && (e164[0] == '1'))
    return '+' + e164;
  return ((e164.length > 2) && (e164[0] == '+')) ? e164 : '';
}

export default {
  name: 'email-phone-field',

  components: {
    ContactsDialog,
    SignInDialog
  },

  props: {
    value: String,
    counts: Object,
    hideContacts: Boolean,
    required: Boolean,
    disabled: Boolean,
    readonly: Boolean,
    fixed: Boolean,
    label: String,
    hint: String,
    rules: { type: Array, default: () => [] }
  },

  data() {
    return {
      staged: '',
      showContacts: false,
      focus: false
    };
  },

  created() {
    Object.assign(this, { mdiCardAccountPhone, mdiAt, mdiMessage, mdiHelp });
  },

  computed: {
    trimmed() {
      // Note that 'trim' itself does not account for some non-printing characters
      return (this.staged || '').trim();
    },

    isValidEmail() {
      return EMAIL_REGEX.test(this.trimmed);
    },

    e164() {
      return getE164(this.trimmed);
    },

    isValidPhone() {
      // Note that we only support US phone numbers
      return (this.e164.length == 12) && this.e164.startsWith('+1') && (this.e164[2] != '1');
    },

    target() {
      if (this.isValidEmail)
        return this.trimmed;
      if (this.isValidPhone)
        return this.e164;
      return '';
    }
  },

  methods: {
    validate() {
      return this.$refs.input.validate();
    },

    async onPaste() {
      // Pasted text can come with non-printing characters, so be sure to strip them. It's easier to only allow a
      // limited set of specific characters, which we can get away with since we know the contents here should only be
      // an email address or phone number. You saw a pasted phone number that looked like this
      // "‭(555) 555-5555‬" which is the cause of the problems. Note that the "standard" regex to match
      // non-printing characters does NOT include those: /[\x00-\x1F\x7F-\x9F]/
      await new Promise(r => setTimeout(r));
      this.staged = (this.staged || '').replace(/[^-+=.~`,()[\]<>;:!@#$%^&*(){}[\]\\|;:'",<>?/\s\w]/g, '').trim();

      // TODO: it may also be worthwhile to automatically extract formatted email addresses like
      // "Alex Exarhos<AlexExarhos@ellacard.com>" or "mailto:" or "tel:" links
    },

    onFocusOut() {
      setTimeout(() => this.focus = false, 200);
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(v) {
        if (this.ignore)
          this.ignore = false;
        else
          this.staged = v && this.$options.filters['email-phone'](v);
      }
    },

    target(v) {
      this.ignore = true;
      this.$emit('input', v);
    }
  }
}
</script>
